import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/solid';
import { UserIcon, CalendarIcon, DocumentDuplicateIcon, TrashIcon } from '@heroicons/react/outline';
import { toast } from 'react-toastify';
import { formatDateShort } from '../../../utils/timestampUtils';
import {
  RMS_PROVIDER_ROLES,
  CLINICIAN_SPECIALISMS,
  CLINICIAN_SPECIALISM_LABELS,
} from '../../../constants/userTypes';
import { apiUpdateProviderUser, apiDeleteProviderUser } from '../../utils/rmsApi';
import { getRmsProviderRoleLabel } from '../../utils/providerRoles';

/**
 * RmsProviderAccountsTable
 * ------------------------
 * Purpose:
 *   Displays a sortable, paginated table of all RMS provider accounts
 *   within the current organisation. Allows copying user IDs for admin purposes.
 *
 * Features:
 *   - Sortable columns (name, user type, specialism, status, date added, registration, role)
 *   - Specialism column for clinicians; editable inline by RMS Org. Admins
 *   - Status column showing 'invited' vs 'active' state with colour-coded badges
 *   - Pagination with 10 items per page
 *   - Copy user ID to clipboard
 *   - Highlights the current user row
 *
 * Props:
 *   - providers: Array of provider objects from rms_organisations/{orgId}/rmsProviderUsers
 *     Each object should have: providerUserId, accountId, firstName, lastName, email,
 *     userType, specialism, status, createdAt, role, registrationBody, registrationNumber
 *   - currentUserId: The UID of the currently logged-in user (for highlighting)
 *   - orgId: The organisation ID (required for the inline specialism edit callable)
 *   - canManageStaff: When true (RMS Org. Admin), clinician specialism and each
 *     provider's free-text role are editable inline; otherwise they render as
 *     read-only text. User-type labels are shown without the internal "RMS "
 *     prefix (e.g. "Clinician").
 */

// Maps status values to display badges with appropriate colour coding
const STATUS_BADGE = {
  active: { label: 'Active', bg: 'bg-green-100', text: 'text-green-800' },
  invited: { label: 'Invited', bg: 'bg-amber-100', text: 'text-amber-800' },
  mfaPending: { label: 'Two-factor pending', bg: 'bg-orange-100', text: 'text-orange-800' },
};

/**
 * Resolve the display badge for a provider's onboarding state.
 *
 * An invited provider who has set their own password is stalled
 * mid-onboarding — either two-factor enrolment hasn't happened yet, or it
 * has but the server-side activation hasn't completed (isMFASetUp can be
 * true while status is still 'invited' if activation failed). Both surface
 * as "Two-factor pending" (distinct from plain "Invited") so org admins can
 * chase or re-invite instead of the account sitting invisible.
 */
function getProviderStatusBadge(provider) {
  if (provider.status === 'invited' && provider.hasSetOwnPassword === true) {
    return STATUS_BADGE.mfaPending;
  }
  return STATUS_BADGE[provider.status] || { label: provider.status || '-', bg: 'bg-gray-100', text: 'text-gray-800' };
}

export default function RmsProviderAccountsTable({ providers = [], currentUserId, orgId, canManageStaff = false }) {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [clickedButton, setClickedButton] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [savingSpecialismId, setSavingSpecialismId] = useState(null);
  // Pending specialism change awaiting admin confirmation when it would release
  // in-progress triages (TD-42): { provider, value, affectedTriageCount }.
  const [specialismChange, setSpecialismChange] = useState(null);
  const [savingRoleId, setSavingRoleId] = useState(null);
  // In-flight role edits keyed by providerUserId. The input is controlled and
  // falls back to provider.role (kept current by onSnapshot) when there is no
  // active draft, so an external update is never masked by a stale defaultValue.
  const [roleDrafts, setRoleDrafts] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);  // provider pending delete confirmation
  const [isDeleting, setIsDeleting] = useState(false);
  const providersPerPage = 10;

  /**
   * Roles whose queue is pathway-scoped by a specialism: clinicians and support.
   * (Org. Admins are not scoped, so they carry no specialism.)
   */
  const roleCarriesSpecialism = useCallback(
    (userType) =>
      userType === RMS_PROVIDER_ROLES.CLINICIAN || userType === RMS_PROVIDER_ROLES.SUPPORT,
    [],
  );

  /**
   * Human-readable specialism label for a provider ('-' for roles that carry no
   * specialism, e.g. Org. Admin).
   */
  const getSpecialismDisplay = useCallback(
    (provider) => {
      if (!roleCarriesSpecialism(provider.userType)) return '-';
      return CLINICIAN_SPECIALISM_LABELS[provider.specialism] || 'Not set';
    },
    [roleCarriesSpecialism],
  );

  /**
   * Apply a specialism change. Reports how many in-progress triages were
   * released (TD-42). The parent's onSnapshot subscription reflects the doc
   * change once the callable resolves.
   */
  const applySpecialism = useCallback(async (provider, value) => {
    setSavingSpecialismId(provider.providerUserId);
    try {
      const res = await apiUpdateProviderUser({
        orgId,
        providerUserId: provider.providerUserId,
        specialism: value,
      });
      const released = res?.unassignedTriageCount || 0;
      if (res?.partial) {
        // Server applied the change but a triage release or the claim sync did
        // not fully land — warn rather than report unqualified success (TD-42).
        toast.warn('Specialism saved, but some changes did not fully apply. Please refresh and verify.');
      } else if (released > 0) {
        toast.success(
          `Specialism updated. Released ${released} in-progress triage${released === 1 ? '' : 's'} to the queue.`,
        );
      } else {
        toast.success('Specialism updated');
      }
    } catch (err) {
      console.error('[RmsProviderAccountsTable] Failed to update specialism:', err);
      toast.error('Failed to update specialism');
    } finally {
      setSavingSpecialismId(null);
    }
  }, [orgId]);

  /**
   * Org. Admin inline edit of a clinician's specialism. Before applying, a dry
   * run checks whether the change would release any of the clinician's
   * in-progress triages (those now outside the new specialism). If so, the admin
   * is warned and must confirm (TD-42); otherwise the change applies directly.
   */
  const handleSpecialismChange = useCallback(async (provider, value) => {
    if (!orgId || value === (provider.specialism || '')) return;
    setSavingSpecialismId(provider.providerUserId);
    try {
      const preview = await apiUpdateProviderUser({
        orgId,
        providerUserId: provider.providerUserId,
        specialism: value,
        dryRun: true,
      });
      const affected = preview?.affectedTriageCount || 0;
      if (affected > 0) {
        // Defer the write until the admin confirms in the warning dialog.
        setSavingSpecialismId(null);
        setSpecialismChange({ provider, value, affectedTriageCount: affected });
        return;
      }
    } catch (err) {
      // Preview failed: fall through and let the real write surface any error.
      console.warn('[RmsProviderAccountsTable] Specialism change preview failed:', err);
    }
    setSavingSpecialismId(null);
    await applySpecialism(provider, value);
  }, [orgId, applySpecialism]);

  /** Confirm a specialism change that releases in-progress triages (TD-42). */
  const confirmSpecialismChange = useCallback(async () => {
    if (!specialismChange) return;
    const { provider, value } = specialismChange;
    // Keep the dialog mounted through the write so its buttons show the saving
    // (disabled) state and the change can't be double-submitted; close on done.
    await applySpecialism(provider, value);
    setSpecialismChange(null);
  }, [specialismChange, applySpecialism]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Helper Functions
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Extracts the registration display string from a provider's fields.
   * Returns "{body}: {number}" if both are present, otherwise "-".
   */
  const getRegistrationDisplay = useCallback((provider) => {
    const body = provider.registrationBody;
    const number = provider.registrationNumber;

    if (body && number) {
      return `${body}: ${number}`;
    }
    return '-';
  }, []);

  /**
   * Extracts the role/job title from a provider's fields.
   */
  const getRole = useCallback((provider) => {
    return provider.role || '-';
  }, []);

  /**
   * Org. Admin inline edit of a provider's free-text role/job title. Applies to
   * every provider type (unlike specialism, which is clinician-only). The
   * parent's onSnapshot subscription reflects the change once it resolves.
   */
  const handleRoleChange = useCallback(async (provider, value) => {
    const id = provider.providerUserId;
    const next = value.trim();
    // Drop the local draft; the controlled input falls back to provider.role,
    // which the parent's onSnapshot keeps current.
    setRoleDrafts((d) => {
      if (!(id in d)) return d;
      const { [id]: _omit, ...rest } = d;
      return rest;
    });
    if (!orgId || next === (provider.role || '')) return;
    setSavingRoleId(id);
    try {
      await apiUpdateProviderUser({ orgId, providerUserId: id, role: next });
      toast.success('Role updated');
    } catch (err) {
      console.error('[RmsProviderAccountsTable] Failed to update role:', err);
      toast.error('Failed to update role');
    } finally {
      setSavingRoleId(null);
    }
  }, [orgId]);

  /**
   * Confirmed delete of a provider user (Org. Admin). The server disables the
   * Auth user + removes the docs; the parent's onSnapshot drops the row.
   */
  const confirmDelete = useCallback(async () => {
    if (!orgId || !deleteTarget) return;
    setIsDeleting(true);
    try {
      await apiDeleteProviderUser({ orgId, providerUserId: deleteTarget.providerUserId });
      toast.success('Provider account deleted');
      setDeleteTarget(null);
    } catch (err) {
      console.error('[RmsProviderAccountsTable] Failed to delete provider:', err);
      toast.error(err?.message || 'Failed to delete provider account');
    } finally {
      setIsDeleting(false);
    }
  }, [orgId, deleteTarget]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Sorting
  // ─────────────────────────────────────────────────────────────────────────────

  const sortedProviders = useMemo(() => {
    let sortable = [...providers];

    if (sortConfig.key) {
      sortable.sort((a, b) => {
        let aValue, bValue;

        switch (sortConfig.key) {
          case 'name':
            aValue = `${a.firstName || ''} ${a.lastName || ''}`.toLowerCase();
            bValue = `${b.firstName || ''} ${b.lastName || ''}`.toLowerCase();
            break;
          case 'userType':
            aValue = getRmsProviderRoleLabel(a).toLowerCase();
            bValue = getRmsProviderRoleLabel(b).toLowerCase();
            break;
          case 'specialism':
            aValue = getSpecialismDisplay(a).toLowerCase();
            bValue = getSpecialismDisplay(b).toLowerCase();
            break;
          case 'status':
            // Sort by the displayed badge label (not raw status) so
            // "Two-factor pending" rows group together rather than
            // interleaving with plain "Invited" rows.
            aValue = getProviderStatusBadge(a).label.toLowerCase();
            bValue = getProviderStatusBadge(b).label.toLowerCase();
            break;
          case 'createdAt':
            aValue = a.createdAt?.toDate?.() || (a.createdAt ? new Date(a.createdAt) : new Date(0));
            bValue = b.createdAt?.toDate?.() || (b.createdAt ? new Date(b.createdAt) : new Date(0));
            return sortConfig.direction === 'ascending'
              ? aValue - bValue
              : bValue - aValue;
          case 'registration':
            aValue = getRegistrationDisplay(a).toLowerCase();
            bValue = getRegistrationDisplay(b).toLowerCase();
            break;
          case 'role':
            aValue = getRole(a).toLowerCase();
            bValue = getRole(b).toLowerCase();
            break;
          default:
            aValue = '';
            bValue = '';
        }

        if (sortConfig.key !== 'createdAt') {
          return sortConfig.direction === 'ascending'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }
        return 0;
      });
    }

    return sortable;
  }, [providers, sortConfig, getRegistrationDisplay, getRole, getSpecialismDisplay]);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return null;
    }
    return sortConfig.direction === 'ascending' ? (
      <ChevronUpIcon className="w-4 h-4 ml-1 text-white" />
    ) : (
      <ChevronDownIcon className="w-4 h-4 ml-1 text-white" />
    );
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Pagination
  // ─────────────────────────────────────────────────────────────────────────────

  const indexOfLastProvider = currentPage * providersPerPage;
  const indexOfFirstProvider = indexOfLastProvider - providersPerPage;
  const currentProviders = sortedProviders.slice(indexOfFirstProvider, indexOfLastProvider);
  const totalPages = Math.ceil(sortedProviders.length / providersPerPage);

  // Clamp the page if the list shrinks (e.g. after a delete) so the user is not
  // stranded on a now-empty page.
  useEffect(() => {
    const pages = Math.max(1, Math.ceil(sortedProviders.length / providersPerPage));
    setCurrentPage((p) => (p > pages ? pages : p));
  }, [sortedProviders.length, providersPerPage]);

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Actions
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Copy the provider's Firebase Auth UID (accountId) to clipboard.
   * Falls back to execCommand for older browser support.
   */
  const copyUserIdToClipboard = async (accountId, providerUserId) => {
    try {
      await navigator.clipboard.writeText(accountId);
      setClickedButton(providerUserId);
      setTimeout(() => setClickedButton(null), 300);
      toast.success('User ID copied to clipboard');
    } catch (err) {
      console.error('Failed to copy user ID:', err);
      const textArea = document.createElement('textarea');
      textArea.value = accountId;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setClickedButton(providerUserId);
      setTimeout(() => setClickedButton(null), 300);
      toast.success('User ID copied to clipboard');
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────────

  if (providers.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
        <UserIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-600">No provider accounts found</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-primary">
          <tr>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider cursor-pointer hover:bg-primary/80 transition-colors"
              onClick={() => requestSort('name')}
            >
              <div className="flex items-center">
                Name
                {getSortIcon('name')}
              </div>
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider cursor-pointer hover:bg-primary/80 transition-colors"
              onClick={() => requestSort('userType')}
            >
              <div className="flex items-center">
                User Type
                {getSortIcon('userType')}
              </div>
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider cursor-pointer hover:bg-primary/80 transition-colors"
              onClick={() => requestSort('specialism')}
            >
              <div className="flex items-center">
                Specialism
                {getSortIcon('specialism')}
              </div>
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider cursor-pointer hover:bg-primary/80 transition-colors"
              onClick={() => requestSort('status')}
            >
              <div className="flex items-center">
                Status
                {getSortIcon('status')}
              </div>
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider cursor-pointer hover:bg-primary/80 transition-colors"
              onClick={() => requestSort('createdAt')}
            >
              <div className="flex items-center">
                Date Added
                {getSortIcon('createdAt')}
              </div>
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider cursor-pointer hover:bg-primary/80 transition-colors"
              onClick={() => requestSort('registration')}
            >
              <div className="flex items-center">
                Registration
                {getSortIcon('registration')}
              </div>
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider cursor-pointer hover:bg-primary/80 transition-colors"
              onClick={() => requestSort('role')}
            >
              <div className="flex items-center">
                Role
                {getSortIcon('role')}
              </div>
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-white uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-100">
          {currentProviders.map((provider) => {
            // accountId is the Firebase Auth UID; compare to currentUserId for highlighting
            const isCurrentUser = provider.accountId === currentUserId;
            const badge = getProviderStatusBadge(provider);

            return (
              <tr
                key={provider.providerUserId}
                className={`hover:bg-gray-50 transition-colors ${
                  isCurrentUser ? 'bg-primary/5' : ''
                }`}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <UserIcon className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-900">
                          {provider.firstName} {provider.lastName}
                        </p>
                        {isCurrentUser && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary">
                            You
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">{provider.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    {getRmsProviderRoleLabel(provider)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {canManageStaff && roleCarriesSpecialism(provider.userType) ? (
                    <select
                      value={provider.specialism || ''}
                      disabled={savingSpecialismId === provider.providerUserId}
                      onChange={(e) => handleSpecialismChange(provider, e.target.value)}
                      aria-label={`Specialism for ${provider.firstName} ${provider.lastName}`}
                      className="text-sm border border-gray-300 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                    >
                      <option value="">Not set</option>
                      {CLINICIAN_SPECIALISMS.map((value) => (
                        <option key={value} value={value}>
                          {CLINICIAN_SPECIALISM_LABELS[value]}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className="text-sm text-gray-600">{getSpecialismDisplay(provider)}</p>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
                    {badge.label}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <CalendarIcon className="h-4 w-4 text-gray-400" />
                    {formatDateShort(provider.createdAt, '-')}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <p className="text-sm text-gray-600">{getRegistrationDisplay(provider)}</p>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {canManageStaff ? (
                    <input
                      type="text"
                      value={roleDrafts[provider.providerUserId] ?? (provider.role || '')}
                      disabled={savingRoleId === provider.providerUserId}
                      onChange={(e) => {
                        const v = e.target.value;
                        setRoleDrafts((d) => ({ ...d, [provider.providerUserId]: v }));
                      }}
                      onBlur={(e) => handleRoleChange(provider, e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                      placeholder="e.g. Psychiatrist"
                      aria-label={`Role for ${provider.firstName} ${provider.lastName}`}
                      className="text-sm border border-gray-300 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 w-40"
                    />
                  ) : (
                    <p className="text-sm text-gray-600">{getRole(provider)}</p>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => copyUserIdToClipboard(provider.accountId, provider.providerUserId)}
                      className={`p-2 transition-all duration-200 border rounded-lg ${
                        clickedButton === provider.providerUserId
                          ? 'text-white bg-primary border-primary'
                          : 'text-primary bg-white border-primary/30 hover:bg-primary/5'
                      }`}
                      aria-label="Copy user ID"
                      title="Copy User ID"
                    >
                      <DocumentDuplicateIcon className="w-4 h-4" />
                    </button>
                    {/* Delete — Org. Admin only, and never your own account (TD-39). */}
                    {canManageStaff && !isCurrentUser && (
                      <button
                        onClick={() => setDeleteTarget(provider)}
                        className="p-2 transition-all duration-200 border rounded-lg text-red-600 bg-white border-red-200 hover:bg-red-50"
                        aria-label={`Delete ${provider.firstName} ${provider.lastName}`}
                        title="Delete provider"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center px-6 py-3 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600">
            Showing <span className="font-medium">{indexOfFirstProvider + 1}</span> to{' '}
            <span className="font-medium">{Math.min(indexOfLastProvider, sortedProviders.length)}</span> of{' '}
            <span className="font-medium">{sortedProviders.length}</span> users
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                currentPage === 1
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
              }`}
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                currentPage === totalPages
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Specialism-change warning (TD-42): releases in-progress triages. */}
      {specialismChange && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Change specialism?</h3>
            <p className="text-sm text-gray-600 mb-5">
              Changing{' '}
              <span className="font-medium">
                {specialismChange.provider.firstName} {specialismChange.provider.lastName}
              </span>
              {"'s"} specialism to{' '}
              <span className="font-medium">
                {CLINICIAN_SPECIALISM_LABELS[specialismChange.value] || 'Not set'}
              </span>{' '}
              will release{' '}
              <span className="font-medium">
                {specialismChange.affectedTriageCount} in-progress triage
                {specialismChange.affectedTriageCount === 1 ? '' : 's'}
              </span>{' '}
              that fall outside the new specialism back to the queue, so another
              clinician can pick them up. This cannot be undone from here.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setSpecialismChange(null)}
                disabled={savingSpecialismId === specialismChange.provider.providerUserId}
                className="px-4 py-2 text-sm font-medium rounded-lg text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmSpecialismChange}
                disabled={savingSpecialismId === specialismChange.provider.providerUserId}
                className="px-4 py-2 text-sm font-medium rounded-lg text-white bg-primary hover:bg-primary/90 disabled:opacity-50"
              >
                Change &amp; release
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation (TD-39) */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" role="dialog" aria-modal="true">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete provider account?</h3>
            <p className="text-sm text-gray-600 mb-5">
              This disables <span className="font-medium">{deleteTarget.firstName} {deleteTarget.lastName}</span>
              {deleteTarget.email ? ` (${deleteTarget.email})` : ''} and removes them from staff management.
              This cannot be undone from here.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium rounded-lg text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
              >
                {isDeleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
